<template>
    <div class="signoff-won-tab">
        <div v-if="loading && !rows.length" class="text-muted">Loading…</div>
        <div v-else-if="!rows.length" class="text-muted">
            <i class="fa fa-check-circle"></i>
            No won quotes awaiting production signoff. You're all clear.
        </div>
        <table v-else class="table table-hover">
            <thead>
                <tr>
                    <th style="width: 60px;">#</th>
                    <th>Quote</th>
                    <th>Total</th>
                    <th>Paid?</th>
                    <th>Won</th>
                    <th style="width: 80px; text-align: center;">Approve</th>
                </tr>
            </thead>
            <tbody>
                <template v-for="row in rows">
                    <tr :key="row.id" @click="toggleExpand(row.id)" style="cursor: pointer;">
                        <td>#{{ row.quote.id }}</td>
                        <td>
                            <strong>{{ row.quote.name }}</strong>
                            <span v-if="row.quote.company && row.quote.company !== row.quote.name" class="text-muted">
                                — {{ row.quote.company }}
                            </span>
                        </td>
                        <td>£{{ formatPounds((row.quote.total_price - row.quote.vat_price) / 100) }}</td>
                        <td>
                            <span v-if="row.quote.xero_paid" class="label label-success">PAID</span>
                            <span v-else class="label label-default">unpaid</span>
                        </td>
                        <td>
                            <small>{{ formatRelative(row.submitted_at) }}</small>
                        </td>
                        <td style="text-align: center;" @click.stop>
                            <input
                                v-if="canApprove"
                                type="checkbox"
                                :disabled="approving[row.id]"
                                @change="onApprove(row, $event)"
                                title="Tick to approve — pick a date below first, or use 'schedule later'"
                            />
                            <span v-else class="text-muted" title="Release is ticked by the Block 2 approvers (Peter, Pete M, Frances)" style="font-size:11px;">—</span>
                            <button
                                v-if="canOverride"
                                type="button"
                                class="btn btn-default btn-xs"
                                style="margin-left: 6px;"
                                :disabled="approving[row.id]"
                                @click.stop="onOverride(row)"
                                title="Override — sign off on behalf of the queued approver with a reason"
                            ><i class="fa fa-shield"></i> Override</button>
                        </td>
                    </tr>
                    <tr v-if="expanded === row.id" :key="row.id + '-detail'">
                        <td colspan="6" style="background: #f8f9fa; padding: 14px 24px;">
                            <preproduction-panel
                                v-if="preproduction"
                                :quote-id="row.quote.id"
                                :can-override="canOverride"
                            />
                            <div v-if="canApprove" class="row" style="margin-bottom: 14px;">
                                <div class="col-sm-12">
                                    <label style="font-weight: bold; margin-right: 8px;">
                                        Production date:
                                    </label>
                                    <input
                                        type="date"
                                        v-model="productionDates[row.id]"
                                        :min="todayIso"
                                        class="form-control"
                                        style="display: inline-block; width: auto; vertical-align: middle;"
                                        :disabled="approving[row.id]"
                                    />
                                    <a
                                        href="#"
                                        @click.prevent="onScheduleLater(row)"
                                        style="margin-left: 14px; font-size: 0.9em;"
                                        :class="{ 'text-muted': approving[row.id] }"
                                    >or schedule later (unassigned)</a>
                                    <div style="margin-top: 4px;">
                                        <small class="text-muted">
                                            Pick a date then tick approve, or click "schedule later" to approve without scheduling.
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <h5>Production capacity</h5>
                                    <capacity-widget :quote-id="row.quote.id" />
                                </div>
                                <div class="col-md-6">
                                    <h5>Client history</h5>
                                    <client-history-widget :quote-id="row.quote.id" />
                                </div>
                            </div>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import CapacityWidget from './CapacityWidget.vue';
import ClientHistoryWidget from './ClientHistoryWidget.vue';
import PreproductionPanel from './PreproductionPanel.vue';

/*
    Won tab — Kev's queue of won quotes awaiting production signoff.
    List + inline detail with capacity + client history widgets,
    tick-to-approve.

    Self-approval is permitted (per design §5.3): Kev may have drafted
    the quote himself; Block 2 is about production readiness, not
    content. Frances/Simon/Peter can override if they want to step in.

    Block 2 also picks a production date on approve. The system then
    schedules + splits + pushes to production in one server hop. The
    "schedule later" link approves without picking a date — the card
    lands in unassigned (today's pre-date-picker behaviour).

    See planning/quote-signoff-design.md §5, §7.4 and
    planning/block2-date-picker-plan.md.
*/
export default {
    components: { CapacityWidget, ClientHistoryWidget, PreproductionPanel },
    props: {
        canOverride: { type: Boolean, default: false },
        // Approvers only (block2/override): shows the release tick.
        // The pre-production team sees the queue + workspace without it.
        canApprove: { type: Boolean, default: true },
    },
    data() {
        return {
            rows: [],
            loading: true,
            expanded: null,
            approving: {},
            productionDates: {},
            preproduction: false,
        };
    },
    computed: {
        todayIso() {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        },
    },
    mounted() { this.refresh(); },
    methods: {
        refresh() {
            this.loading = true;
            fetch('/api/signoff/won/list', { headers: { Accept: 'application/json' }})
                .then(r => r.json())
                .then(d => { this.rows = d.data || d; this.preproduction = !!d.preproduction_enabled; this.loading = false; })
                .catch(() => { this.loading = false; });
        },
        toggleExpand(rowId) {
            this.expanded = this.expanded === rowId ? null : rowId;
        },
        onApprove(row, event) {
            if (this.approving[row.id]) return;
            const date = (this.productionDates[row.id] || '').trim();
            if (!date) {
                alert('Pick a production date first — or click "schedule later" to approve without scheduling.');
                if (event && event.target) event.target.checked = false;
                return;
            }
            this.postApprove(row, { production_date: date });
        },
        onScheduleLater(row) {
            if (this.approving[row.id]) return;
            if (!window.confirm('Approve this quote for production WITHOUT picking a date?\n\nThe card will land in "unassigned" on the calendar and someone will need to schedule it later.')) {
                return;
            }
            this.postApprove(row, {});
        },
        postApprove(row, body) {
            this.$set(this.approving, row.id, true);

            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/quote/${row.quote.id}/signoff/won/approve`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
                body: JSON.stringify(body || {}),
            })
                .then(r => r.json().then(b => ({ status: r.status, body: b })))
                .then(({ status, body }) => {
                    if (status >= 400) {
                        alert(body.error || 'Approve failed');
                        this.$set(this.approving, row.id, false);
                        return;
                    }
                    this.flashScheduleOutcome(body);
                    this.rows = this.rows.filter(r => r.id !== row.id);
                    if (this.expanded === row.id) this.expanded = null;
                    this.$emit('approved', { quoteId: row.quote.id, stage: 'won' });
                })
                .catch(err => {
                    alert('Approve failed: ' + (err.message || 'network error'));
                    this.$set(this.approving, row.id, false);
                });
        },
        onOverride(row) {
            if (this.approving[row.id]) return;

            const date = (this.productionDates[row.id] || '').trim();
            let production_date = null;

            if (!date) {
                if (!window.confirm('Override approve WITHOUT a production date?\n\nThe card will land in "unassigned" on the calendar and someone will need to schedule it later.')) {
                    return;
                }
            } else {
                production_date = date;
            }

            const reason = window.prompt(
                'OVERRIDE production signoff for #' + row.quote.id + ' — sign off on behalf of the queued approver.\n\nReason (min 10 chars):',
                ''
            );
            if (reason === null || reason.trim().length < 10) {
                if (reason !== null) alert('Override cancelled — reason must be at least 10 characters.');
                return;
            }
            this.$set(this.approving, row.id, true);

            const body = { override_reason: reason.trim() };
            if (production_date) body.production_date = production_date;

            const token = document.querySelector('meta[name="csrf-token"]')?.content || '';
            fetch(`/quote/${row.quote.id}/signoff/won/override`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': token,
                },
                body: JSON.stringify(body),
            })
                .then(r => r.json().then(b => ({ status: r.status, body: b })))
                .then(({ status, body }) => {
                    if (status >= 400) {
                        alert((body && (body.message || body.error)) || 'Override failed');
                        this.$set(this.approving, row.id, false);
                        return;
                    }
                    this.flashScheduleOutcome(body);
                    this.rows = this.rows.filter(r => r.id !== row.id);
                    if (this.expanded === row.id) this.expanded = null;
                    this.$emit('approved', { quoteId: row.quote.id, stage: 'won', override: true });
                })
                .catch(err => {
                    alert('Override failed: ' + (err.message || 'network error'));
                    this.$set(this.approving, row.id, false);
                });
        },
        flashScheduleOutcome(body) {
            if (body.print && body.print.ok === false) {
                alert('Approved ✓ — but production deal label print FAILED. Check the printer service.');
            }
            if (body.schedule && body.schedule.ok === false) {
                alert(
                    'Approved ✓ — but scheduling FAILED: ' + (body.schedule.reason || 'unknown error') +
                    '\n\nYou will need to schedule this quote manually.'
                );
            }
        },
        formatRelative(iso) {
            if (!iso) return '';
            const then = new Date(iso).getTime();
            const diffMins = Math.floor((Date.now() - then) / 60000);
            if (diffMins < 60) return `${diffMins}m ago`;
            const hours = Math.floor(diffMins / 60);
            if (hours < 24) return `${hours}h ago`;
            return `${Math.floor(hours / 24)}d ago`;
        },
        formatPounds(n) {
            return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>
